import type { InvalidateQueryFilters } from "@tanstack/react-query";
import type { DataSession } from "../../data/core/session";
import { getQueryKey } from "@trpc/react-query";
import { gamificationSummaryQueryKey } from "@hooks/trpc/useGamificationSummary/queryFunctions";
import { trpc } from "@utils/trpc";
import type { RealtimeCacheInvalidationCommand } from "./types";

export function profileStatsQueryKey() {
  return getQueryKey(trpc.user.stats.getForMe);
}

export function inboxListQueryKey() {
  return getQueryKey(trpc.user.inbox.listItems);
}

export function inboxUnreadQueryKey() {
  return getQueryKey(trpc.user.inbox.hasUnread);
}

export function accessDetailQueryKey(accessId: string) {
  return getQueryKey(trpc.accesses.getById, { accessId }, "query");
}

export function accessListQueryKeys() {
  return [
    getQueryKey(trpc.accesses.listAll, undefined, "query"),
    getQueryKey(trpc.accesses.listAllPaginated),
    getQueryKey(trpc.accesses.listPinned, undefined),
  ];
}

export function accessOwnershipQueryKeys() {
  return [
    getQueryKey(trpc.user.accesses.listUserAccesses, undefined),
    getQueryKey(trpc.user.accesses.listUnlockedPrivateSongs),
  ];
}

export function presenceRoomQueryKey(accessId: string) {
  return getQueryKey(trpc.user.liveRoom.getActiveConnections, { accessId });
}

export function liveRoomStateQueryKey(accessId: string) {
  return getQueryKey(trpc.user.liveRoom.get, { accessId });
}

export function getRealtimeInvalidationFilters(
  changes: RealtimeCacheInvalidationCommand[],
): InvalidateQueryFilters[] {
  const filters: InvalidateQueryFilters[] = [];
  const seenQueryKeys = new Set<string>();

  const addFilter = (filter: InvalidateQueryFilters) => {
    const key = JSON.stringify(filter.queryKey);
    if (seenQueryKeys.has(key)) return;
    seenQueryKeys.add(key);
    filters.push(filter);
  };

  for (const change of changes) {
    switch (change.scope) {
      case "gems.balance":
        addFilter({ queryKey: gamificationSummaryQueryKey() });
        break;
      case "gamification.summary":
        addFilter({ queryKey: gamificationSummaryQueryKey() });
        break;
      case "profile.stats":
        addFilter({ queryKey: profileStatsQueryKey() });
        break;
      case "access.detail":
        if (change.params?.accessId) {
          addFilter({
            queryKey: accessDetailQueryKey(change.params.accessId),
          });
        }
        break;
      case "access.lists":
        for (const queryKey of accessListQueryKeys()) {
          addFilter({ queryKey });
        }
        break;
      case "access.ownership":
        for (const queryKey of accessOwnershipQueryKeys()) {
          addFilter({ queryKey });
        }
        break;
      case "inbox.list":
        addFilter({ queryKey: inboxListQueryKey() });
        break;
      case "inbox.unread":
        addFilter({ queryKey: inboxUnreadQueryKey() });
        break;
      case "liveRoom.state":
        if (change.params?.accessId) {
          addFilter({
            queryKey: liveRoomStateQueryKey(change.params.accessId),
          });
        }
        break;
      case "presence.room":
        if (change.params?.accessId) {
          addFilter({
            queryKey: presenceRoomQueryKey(change.params.accessId),
          });
        }
        break;
    }
  }

  return filters;
}

export async function invalidateRealtimeChanges({
  changes,
  data,
}: {
  changes: RealtimeCacheInvalidationCommand[];
  data: DataSession;
}) {
  const filters = getRealtimeInvalidationFilters(changes);

  for (const filter of filters) {
    if (!data.isCurrent()) return;
    await data.queryClient.invalidateQueries(filter);
  }
}
